import { Grains, Plus, Trash, ArrowFatLineUp, UploadSimple, DownloadSimple, PencilSimple } from "@phosphor-icons/react";
import { StockBadge, MinCell, StockInForm, AdjustStockForm } from "./widgets";

const UNITS = ["kg", "g", "litre", "ml", "pcs"];

export const RawMaterialsPanel = ({
  rawMaterials, selectedRaws, setSelectedRaws, rawForm, setRawForm, addRaw,
  bulkDeleteRaws, deleteRaw, downloadRawTemplate, importRaws, rawFileRef,
  adjustFor, setAdjustFor, stockInFor, setStockInFor, adjustStock, stockIn, setMin,
}) => (
  <div className="rounded-md border border-slate-200 bg-white" data-testid="raw-materials-panel">
    <div className="flex items-center justify-between border-b border-slate-200 p-4">
      <h2 className="flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
        <Grains size={18} weight="duotone" /> Raw Materials
      </h2>
      <div className="flex items-center gap-2">
        {selectedRaws.length > 0 && (
          <button onClick={bulkDeleteRaws} data-testid="raw-bulk-delete-button"
            className="flex items-center gap-1.5 rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors duration-150 hover:bg-red-700">
            <Trash size={13} weight="bold" /> Delete selected ({selectedRaws.length})
          </button>
        )}
        <button onClick={downloadRawTemplate} data-testid="raw-template-button"
          title="Download a sample Excel: Name | Unit | Opening Stock"
          className="flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
          <DownloadSimple size={13} weight="bold" /> Template
        </button>
        <input ref={rawFileRef} type="file" accept=".xlsx" className="hidden"
          data-testid="raw-import-input" onChange={importRaws} />
        <button onClick={() => rawFileRef.current?.click()} data-testid="raw-import-button"
          className="flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
          <UploadSimple size={13} weight="bold" /> Import Excel
        </button>
        <span className="text-xs text-slate-400">{rawMaterials.length} entries</span>
      </div>
    </div>
    <div className="flex flex-wrap gap-2 border-b border-slate-100 p-3">
      <input value={rawForm.name} onChange={(e) => setRawForm((f) => ({ ...f, name: e.target.value }))}
        placeholder="Raw material name" data-testid="raw-name-input"
        className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
      <select value={rawForm.unit} onChange={(e) => setRawForm((f) => ({ ...f, unit: e.target.value }))}
        data-testid="raw-unit-select"
        className="rounded-md border border-slate-300 bg-white px-2 py-2 text-sm outline-none">
        {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
      </select>
      <input type="number" min="0" step="any" value={rawForm.stock}
        onChange={(e) => setRawForm((f) => ({ ...f, stock: e.target.value }))}
        placeholder="Opening stock" data-testid="raw-stock-input"
        className="w-28 rounded-md border border-slate-300 px-2 py-2 text-sm outline-none" />
      <button onClick={addRaw} data-testid="raw-add-button"
        className="flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700">
        <Plus size={14} weight="bold" /> Add
      </button>
    </div>
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
          <th className="w-10 p-3">
            <input type="checkbox" data-testid="raw-select-all"
              checked={rawMaterials.length > 0 && selectedRaws.length === rawMaterials.length}
              onChange={() => setSelectedRaws(selectedRaws.length === rawMaterials.length ? [] : rawMaterials.map((rm) => rm.id))}
              className="h-4 w-4 accent-slate-900" />
          </th>
          <th className="p-3">Name</th>
          <th className="p-3">Unit</th>
          <th className="p-3 text-right">Stock</th>
          <th className="p-3 text-right">Min</th>
          <th className="p-3 text-right">Stock In</th>
          <th className="p-3" />
        </tr>
      </thead>
      <tbody data-testid="raw-materials-table-body">
        {rawMaterials.length === 0 && (
          <tr><td colSpan={7} className="p-5 text-center text-slate-400">No raw materials yet.</td></tr>
        )}
        {rawMaterials.map((r) => (
          <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="w-10 p-3">
              <input type="checkbox" data-testid={`raw-select-${r.name}`}
                checked={selectedRaws.includes(r.id)}
                onChange={() => setSelectedRaws((p) => (p.includes(r.id) ? p.filter((x) => x !== r.id) : [...p, r.id]))}
                className="h-4 w-4 accent-slate-900" />
            </td>
            <td className="p-3 font-medium">{r.name}</td>
            <td className="p-3 text-slate-500">{r.unit}</td>
            <td className="p-3 text-right">
              {adjustFor === `raw-${r.id}` ? (
                <AdjustStockForm current={r.stock} testId={`raw-adjust-${r.name}`}
                  onSubmit={(v, n) => adjustStock("raw", r.id, v, n)} onCancel={() => setAdjustFor(null)} />
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <StockBadge stock={r.stock} min={r.min_stock || 0} testId={`raw-stock-${r.name}`} />
                  <button onClick={() => setAdjustFor(`raw-${r.id}`)} data-testid={`raw-adjust-button-${r.name}`}
                    title="Correct this stock value"
                    className="p-0.5 text-slate-400 transition-colors duration-150 hover:text-slate-700">
                    <PencilSimple size={13} weight="duotone" />
                  </button>
                </span>
              )}
            </td>
            <td className="p-3 text-right">
              <MinCell value={r.min_stock || 0} testId={`raw-min-${r.name}`}
                onSave={(v) => setMin("raw", r.id, v)} />
            </td>
            <td className="p-3 text-right">
              {stockInFor === `raw-${r.id}` ? (
                <StockInForm testId={`raw-stockin-${r.name}`}
                  onSubmit={(q, n) => stockIn("raw", r.id, q, n)} onCancel={() => setStockInFor(null)} />
              ) : (
                <button onClick={() => setStockInFor(`raw-${r.id}`)} data-testid={`raw-stockin-button-${r.name}`}
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white hover:bg-slate-700">
                  <ArrowFatLineUp size={12} weight="bold" /> Stock In
                </button>
              )}
            </td>
            <td className="p-3 text-right">
              <button onClick={() => deleteRaw(r)} data-testid={`raw-delete-button-${r.name}`}
                className="p-1 text-slate-400 transition-colors duration-150 hover:text-red-600">
                <Trash size={15} weight="duotone" />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
